import { z } from "zod";
import { adContextSchema, type AdContext, type AdEvent } from "@libs/attribution-contracts";
import { minorToMajor } from "@libs/money";

/** Use the same persisted order fact for both browser and server conversions. */
export function adEventForOrder(
  order: {
    orderReference: string;
    source: string;
    status: string;
    accessId: string;
    accessType: string;
    amountTotal: string | number | null;
    currency: string | null;
    confirmedAt: string;
    adContext: AdContext | null;
  },
  artistId: string,
): AdEvent | null {
  const context = adContextSchema.safeParse(order.adContext);
  if (!context.success || !artistId) return null;
  if (
    order.status !== "fulfilled" &&
    !(order.source === "engine-checkout" && order.status === "confirmed")
  )
    return null;
  if (
    !["engine-checkout", "stripe-checkout", "free-checkout"].includes(
      order.source,
    )
  )
    return null;
  const amount = order.amountTotal === null ? null : Number(order.amountTotal);
  if (amount !== null && (!Number.isFinite(amount) || amount < 0)) return null;
  const free =
    amount === 0 || (order.source === "free-checkout" && amount === null);
  if (order.source === "free-checkout" && !free) return null;
  if (
    !free &&
    (amount === null ||
      !Number.isFinite(amount) ||
      amount <= 0 ||
      !order.currency)
  )
    return null;
  const time = Math.floor(Date.parse(order.confirmedAt) / 1000);
  if (!Number.isFinite(time)) return null;
  return {
    name: free ? "FreeUnlock" : "Purchase",
    id: `order:${order.orderReference}`,
    time,
    sourceUrl: context.data.sourceUrl,
    artistId,
    accessId: order.accessId,
    contentType: order.accessType === "event" ? "event" : "access",
    ...(!free
      ? { value: amount!, currency: order.currency!.toUpperCase() }
      : {}),
  };
}

/**
 * The conversion of a vendor (buy-on-behalf) attempt the vendor confirmed, from
 * the attempt's own facts: the total the buyer approved and the moment it
 * reached `bought`. Its conversion ID is the reference its tickets carry
 * (`vendor:<attemptId>`); a zero-total order is a value-neutral FreeUnlock.
 */
export function adEventForVendorPurchase(
  attempt: {
    id: string;
    phase: string;
    accessId: string;
    offer: unknown;
    progress: unknown;
    adContext: AdContext | null;
  },
  artistId: string,
): AdEvent | null {
  const context = adContextSchema.safeParse(attempt.adContext);
  if (!context.success || !artistId || attempt.phase !== "bought") return null;
  const offer = z
    .object({
      totalMinor: z.number().int().nonnegative(),
      currency: z.string().regex(/^[A-Za-z]{3}$/),
    })
    .safeParse(attempt.offer);
  const bought = z
    .array(z.object({ phase: z.string(), at: z.string() }))
    .safeParse(attempt.progress);
  if (!offer.success || !bought.success) return null;
  // A closed phase is published once, so there is one such milestone.
  const time = Math.floor(
    Date.parse(bought.data.find((m) => m.phase === "bought")?.at ?? "") / 1000,
  );
  if (!Number.isFinite(time)) return null;
  const currency = offer.data.currency.toUpperCase();
  const free = offer.data.totalMinor === 0;
  return {
    name: free ? "FreeUnlock" : "Purchase",
    id: `order:vendor:${attempt.id}`,
    time,
    sourceUrl: context.data.sourceUrl,
    artistId,
    accessId: attempt.accessId,
    contentType: "event",
    ...(!free ? { value: minorToMajor(offer.data.totalMinor, currency), currency } : {}),
  };
}

/** Engine amounts use the currency's exponent, including zero-decimal currencies. */
export function adValueFromMinor(amount: number, currency: string): number {
  const digits =
    new Intl.NumberFormat("en", {
      style: "currency",
      currency,
    }).resolvedOptions().maximumFractionDigits ?? 2;
  return amount / 10 ** digits;
}
